import type React from "react";
import { useCallback, useRef, useState } from "react";
import type { GraphStatistics } from "@/features/graph/components/controls/StatisticsPanel";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";

interface UseNotebookLayoutStateReturn {
	selectedGraphId: string | null;
	setSelectedGraphId: React.Dispatch<React.SetStateAction<string | null>>;
	selectedSources: Set<string>;
	setSelectedSources: React.Dispatch<React.SetStateAction<Set<string>>>;
	graphStatistics: GraphStatistics | null;
	setGraphStatistics: React.Dispatch<React.SetStateAction<GraphStatistics | null>>;
	graphNodes: VisualNode[];
	setGraphNodes: React.Dispatch<React.SetStateAction<VisualNode[]>>;
	graphEdges: VisualEdge[];
	setGraphEdges: React.Dispatch<React.SetStateAction<VisualEdge[]>>;
	isEditingPanelVisible: boolean;
	setIsEditingPanelVisible: React.Dispatch<React.SetStateAction<boolean>>;
	hasAutoSelectedRef: React.MutableRefObject<boolean>;
	handleSourceToggle: (sourceId: string) => void;
	handleSelectAll: (selected: boolean, documentIds: string[]) => void;
	handleStatisticsChange: (stats: GraphStatistics) => void;
	handleNodesEdgesChange: (nodes: VisualNode[], edges: VisualEdge[]) => void;
}

export const useNotebookLayoutState = (): UseNotebookLayoutStateReturn => {
	const [selectedGraphId, setSelectedGraphId] = useState<string | null>(null);
	const [selectedSources, setSelectedSources] = useState<Set<string>>(new Set());
	const [graphStatistics, setGraphStatistics] = useState<GraphStatistics | null>(null);
	const [graphNodes, setGraphNodes] = useState<VisualNode[]>([]);
	const [graphEdges, setGraphEdges] = useState<VisualEdge[]>([]);
	const hasAutoSelectedRef = useRef(false);
	const [isEditingPanelVisible, setIsEditingPanelVisible] = useState(true);

	const handleSourceToggle = useCallback((sourceId: string) => {
		setSelectedSources((prev) => {
			const next = new Set(prev);
			if (next.has(sourceId)) {
				next.delete(sourceId);
			} else {
				next.add(sourceId);
			}
			return next;
		});
	}, []);

	const handleSelectAll = useCallback((selected: boolean, documentIds: string[]) => {
		if (selected) {
			setSelectedSources(new Set(documentIds));
		} else {
			setSelectedSources(new Set());
		}
	}, []);

	const handleStatisticsChange = useCallback((stats: GraphStatistics) => {
		setGraphStatistics(stats);
	}, []);

	const handleNodesEdgesChange = useCallback((nodes: VisualNode[], edges: VisualEdge[]) => {
		setGraphNodes(nodes ?? []);
		setGraphEdges(edges ?? []);
	}, []);

	return {
		selectedGraphId,
		setSelectedGraphId,
		selectedSources,
		setSelectedSources,
		graphStatistics,
		setGraphStatistics,
		graphNodes,
		setGraphNodes,
		graphEdges,
		setGraphEdges,
		isEditingPanelVisible,
		setIsEditingPanelVisible,
		hasAutoSelectedRef,
		handleSourceToggle,
		handleSelectAll,
		handleStatisticsChange,
		handleNodesEdgesChange,
	};
};

export default useNotebookLayoutState;
